/**
 * DRM v3 data model (API Gateway / events-server).
 * Mirrors `callan.api.v3.model.drm` contracts exposed via `EventsDrm` (`/api/v1`).
 * Types are prefixed to avoid collisions with the legacy models in `books/model/book-model.ts`
 * (e.g. legacy `ProductShare` carries `magicNumber`, the v3 one does not).
 */

export const DRM_PLATFORM_CALLAN_CASA = 'CALLAN_CASA';

export interface V3PlatformRef {
  name: string;
}

// ---- Accounts ---------------------------------------------------------------

export interface V3DrmAccountRef {
  /**
   * Internal DRM account id. It is NOT equal to the CASA account id;
   * resolve it via `GET /api/v1/accounts?platform=CALLAN_CASA&id=<casaAccountId>`.
   */
  id: number;
}

export interface V3DrmAccountDetails {
  name?: string;
  email?: string;
}

export enum V3DrmAccountStatus {
  ACTIVATING = 'ACTIVATING',
  ACTIVE = 'ACTIVE',
  LOCKED = 'LOCKED',
}

export interface V3DrmAccount extends V3DrmAccountRef, V3DrmAccountDetails {
  status: V3DrmAccountStatus;
  lastRefreshTime: string;
  primaryAccountId: number;
  adminAccount: boolean;
}

export interface V3AccountLink extends V3PlatformRef {
  /** Account identifier in the platform namespace (usually the CASA account id). */
  accountId: string;
  email: string;
}

// ---- Products ---------------------------------------------------------------

export interface V3DrmProductRef {
  id: number;
}

export interface V3DrmProductDetails {
  name: string;
  description: string;
  order: number;
  labels: string;
  categories: string[];
}

export interface V3DrmProduct extends V3DrmProductRef, V3DrmProductDetails {
  /** Publish marker: null means unpublished. */
  publicationDate: string;
  /** Visibility for users who do not own the product. */
  displayPublic: boolean;
  /** Logical `productPath` consumed by the reader content endpoints. */
  path: string;
}

export interface V3ProductVersionRef {
  id: number;
}

export interface V3ProductVersion extends V3ProductVersionRef {
  createDate: string;
}

export interface V3ProductVersionEpubFileDetails {
  name: string;
  remoteName: string;
  size: number;
  uploadDate: string;
  title: string;
  description: string;
  baseDir: string;
  coverFile: string;
  audioSize: number;
  dictationSize: number;
}

// ---- Shares -----------------------------------------------------------------

export interface V3ManualOrderRef {
  id: number;
}

export interface V3LinkedProductShareRef extends V3PlatformRef {
  id: number;
}

export interface V3ProductShareRef {
  id: number;
}

/**
 * Account entitlement to a single product. Merged view of shares created from
 * platform links (`linkedShareRef`) and DRM manual orders (`manualOrderRef`).
 */
export interface V3ProductShare extends V3ProductShareRef {
  product: V3DrmProduct;
  manualOrderRef?: V3ManualOrderRef;
  linkedShareRef?: V3LinkedProductShareRef;
  /** Remaining device slots for this share (max 3 devices per share). */
  emptySlots: number;
}

// ---- Devices & encryption keys ----------------------------------------------

export interface V3DeviceRef {
  /** Stable DRM device identifier tied to a single DRM account. */
  deviceId: string;
}

export interface V3DeviceRegistration extends V3DeviceRef {
  /** Signed device authentication token (`X-Device-Key`) used in the v2 protocol. */
  deviceKey: string;
}

export interface V3DeviceRegistrationRequest {
  platform: string;
  fingerprintVector?: { [key: string]: string };
}

export enum V3EncryptionKeyStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  LOCKED = 'LOCKED',
  DELETED = 'DELETED',
}

export interface V3EncryptionKeyRef {
  /** Server-generated key identifier (UUID). */
  keyId: string;
}

export interface V3ShareDownloadRef {
  id: number;
}

export interface V3EncryptionKey extends V3EncryptionKeyRef {
  status: V3EncryptionKeyStatus;
  /** Null for PENDING keys; present once the key is assigned in the download flow. */
  downloadAssigned?: V3ShareDownloadRef;
}

export interface V3EncryptionKeyValue extends V3EncryptionKey {
  /** Signed encryption token (`X-Encryption-Key`), short-lived (~3 minutes). */
  key: string;
  /** Server ECDH public key (Base64) used to derive the shared secret. */
  publicServerKey: string;
}

// ---- Downloads ----------------------------------------------------------------

export interface V3ShareDownloadDetails {
  deviceRef: V3DeviceRef;
}

export interface V3ShareDownload extends V3ShareDownloadRef, V3ShareDownloadDetails {
  date: string;
  productVersion: V3ProductVersionRef;
  encryptKey: V3EncryptionKeyRef;
}

// ---- View model ---------------------------------------------------------------

/**
 * Book entry in the v3 view model, returned by `BooksNativeServiceApiV3`.
 * The core is the v3 DTO (`share` with its embedded `product`); the optional fields
 * carry information which - depending on the platform - comes from outside the share:
 * on mobile from the legacy native bridge, on web from additional v3 reads.
 */
export interface BookListEntry {
  share: V3ProductShare;
  /** Current release id - present only when data comes from the native bridge (mobile). */
  releaseId?: number;
  /** Local download state - present only when data comes from the native bridge (mobile). */
  downloaded?: boolean;
  /** Whether the account owns the product (has a share / legacy `hasProduct`). */
  hasProduct?: boolean;
  /** Number of device slots in use (v3: registered downloads, legacy: `devicesShared`). */
  usedDevices?: number;
}
